import {
    CustomDataSource,
    defined,
    VerticalOrigin,
    HeightReference,
    HorizontalOrigin,
    Color
} from 'cesium';
import {createFeatureCollection, getDataByHttpByParams} from './createFeatureCollection';
import Label from '../label/Label';
import Marker from '../marker/Marker';
import Polyline from '../polyline/Polyline';
import Polygon from '../polygon/Polygon';
import {setRenderer, getImgUrl} from '../renderer';
import isObject from '../_util/isObject';
import isFunction from '../_util/isFunction';
import { getVectorFeatureInfoByText } from '../_util/layer';
import encryptParams from '../_util/encryptParams';
import customClusterStyle from './customClusterStyle';
import transformRgb from "../_util/transformRgb";

const P = {
    Label: Label,
    Point: Marker,
    Marker: Marker,
    Polyline: Polyline,
    Polygon: Polygon,
    LineString: Polyline,
    MultiLineString: Polyline,
};

const createFeature = v => {
    const {geometry, label} = v;
    let { type, coordinates } = geometry;
    if (P[type]) {
        return new P[type]({ geometry: coordinates, properties: v, label });
    }
};

class FeatureLayer extends CustomDataSource {
    constructor(opt_options) {
        const options = opt_options || {};
        const {
            url,
            params,
            data,
            key,
            name,
            dataField,
            label,
            labelField,
            template,
            autoRefresh,
            method,
            renderMode,
            style,
            markerStyle,
            show = true,
            layerControl = true,
            afterGetData,
            viewOnly,
            legend,
            group,
            map,
            cluster,
            zIndex,
            cancelPopup,
            encrypt,
            tipTemplate,
        } = options;

        let defaultShow = show;
        if (group?.length) {
            defaultShow = false;

            if (!show) {
                group.map(f => {
                    f.show = false;
                });
            } else {
                group.map(f => {
                    f.show = f.show ?? true;

                    if (f.show) {
                        defaultShow = true;
                    }
                });
            }
        }

        super(options);

        // 参数
        this._options = opt_options;

        super.show = show;

        // 接口地址
        this.url = url;

        // 接口地址请求类型
        this.method = method;

        // 接口查询参数
        this.params = params;

        // 图层标识
        this.key = key;

        // 图层名称
        this.name = name;

        // 渲染
        this.style = style;

        //marker样式
        this.markerStyle = markerStyle;

        // 标签
        this.label = label;

        // 标签字段
        this.labelField = labelField;

        // 自动刷新标识
        this.autoRefresh = autoRefresh;

        // 模版内容
        this.template = template;

        // 渲染模式
        this.renderMode = renderMode || 'marker';

        // 图例
        this.legend = legend;

        // 过滤
        this.group = group;

        // 地图
        this.map = map;

        // 图层控制
        this.layerControl = layerControl;

        // 聚合
        this.cluster = cluster;

        // 层级
        this.zIndex = zIndex;

        // 数据
        this.data = data;

        // 数据取值字段
        this.dataField = dataField || 'geometryInfo';

        // 处理获取的数据
        this.afterGetData = afterGetData;

        // 是否初始化
        this.inited = false;

        // 取消气泡
        this.cancelPopup = cancelPopup;

        // 加密
        this.encrypt = encrypt;

        // 气泡模板
        this.tipTemplate = tipTemplate;

        // 仅展示
        this.viewOnly = viewOnly;

        // 默认是否展示
        if (defaultShow) {
            this.init();
        }
    }

    // 重写父类的属性访问器
    get show() {
        return super.show;
    }

    set show(value) {
        this.setVisible(value);
    }

    set label(val) {
        this._options.label = val;
        this.setStyle();
    }

    // 初始化数据
    init() {
        // 是否存在地址
        if (this.url) {
            // 执行
            this.loadUrlData(this.url, this.params, this.method);
        }

        // 直接加载数据
        if (this.data) {
            // 执行
            this.loadData(this.data);
        }

        this.inited = true;
    }

    // 获取地图
    getMap() {
        return this.map;
    }

    // 根据条件获取图形数据
    getFeatureByFieldValue(field, value) {
        return this.getFeatures().find(
            v => String(v?.properties?.[field]?._value) === String(value),
        );
    }

    // 获取当前图层的所有图形
    getFeatures() {
        return this.entities.values;
    }

    // 根据条件获取图形数据，用于地图搜索
    getFeaturesBySearch(field, value) {
        return getVectorFeatureInfoByText(this, field, value);
    }

    // 设置图层显示隐藏
    setVisible(visible) {
        super.show = visible; // 将属性传递给父类

        const map = this.getMap && this.getMap();
        if (map) {
            map.layers.emit('visible', this, visible);
        }

        // 判断是否完成过初始化，没有的话就加载一波数据
        if (!this.inited) {
            this.init();
        }
    }

    // 设置过滤条件
    setGroup(group) {
        const map = this.getMap && this.getMap();

        if (group?.length) {
            const features = this.getFeatures();
            if (features?.length) {
                features.map(feature => {
                    let show = false;
                    group.map(v => {
                        if (!v.hasOwnProperty('show') || v.show) {
                            let properties = map.getProperties(feature);
                            if (v.filter(properties)) {
                                show = true;
                            }
                        }
                    });

                    feature.show = show;
                });
            }

            this.group = group;

            this.setVisibleByGroup();
        }
    }

    // 根据过滤条件组显示图层
    setVisibleByGroup() {
        const group = this.group;
        if (group?.length) {
            let visible = false;

            group.map(v => {
                if (!v.hasOwnProperty('show') || v.show) {
                    visible = true;
                }
            });

            this.setVisible(visible);
        }
    }

    // 设置渲染模式
    setRenderMode(renderMode) {
        this.hideClustering();

        // 展示聚合时设置样式为 marker 并且开启聚合监听
        if (renderMode === 'clustering') {
            this.renderMode = 'marker';
            this.setStyle();
            this.showClustering();
        } else {
            this.renderMode = renderMode;
            this.setStyle();
        }
    }

    // 自动刷新
    refresh() {
        let params = this.params;
        if (this.encrypt) {
            params = encryptParams(params, this.encrypt);
        }
        getDataByHttpByParams({
            url: this.url,
            params,
            method: this.method,
            afterGetData: this.afterGetData,
            field: this.dataField,
        }).then(res => {
            if (res?.features?.length) {
            }
        });
    }

    // 加载数据
    loadData(data) {
        this.entities.removeAll();
        let geoJsonData = createFeatureCollection(Array.isArray(data) ? data : [data], this.dataField);
        this.render(geoJsonData);
    }

    // 加载接口数据
    loadUrlData(url, params, method, afterGetData = this.afterGetData) {
        this.entities.removeAll();
        if (this.encrypt) {
            params = encryptParams(params, this.encrypt);
        }
        getDataByHttpByParams({
            url,
            params,
            method,
            afterGetData,
            field: this.dataField,
        }).then(res => {
            if (res?.features?.length) {
                this.render(res);
            }
        });
    }

    // 加载图形数据
    loadFeatures(features) {
        this.entities.removeAll();
        this.entities.addFeatures(features);
    }

    // 渲染
    render(geoJsonData) {
        this.geoJsonData = geoJsonData;
        if (geoJsonData) {
            if (this.renderMode === 'marker') {
                this.setStyle();
            } else {
                this.setRenderMode(this.renderMode);
            }
        }
    }

    getLabelStyle(v) {
        const label = this._options.label;
        if (label) {
            if (isFunction(label)) {
                return label(v);
            }

            const labelOptions = typeof label === 'object' ? label : {};
            return {
                text: v.properties[labelOptions.labelField || this.labelField],
                size: 12,
                color: '#FFF',
                background: 'rgb(0,128,255)',
                pixelOffset: {x: 0, y: 10},
                ...labelOptions,
            };
        }

        return null
    }

    // 设置样式
    setStyle() {
        this.entities.removeAll();
        let { features } = this.geoJsonData || {};
        const group = this.group;

        if (features?.length) {
            features.map(v => {
                let entity = createFeature({...v, label: this.getLabelStyle(v)});
                if (entity) {
                    let show = true;
                    let style = this.style;

                    // 针对过滤时的处理
                    if (this.group?.length) {
                        show = false;
                        group.map(f => {
                            if (f.filter(v.properties)) {
                                style = f.style;

                                if (!f.hasOwnProperty('show') || f.show) {
                                    show = true;
                                }
                            }
                        });
                    }
                    if (this.renderMode === 'marker') {
                        setRenderer(entity, style);

                        if (style) {
                            let _style = style;
                            if (isFunction(style)) {
                                _style = style(entity);
                            }

                            if (_style.type === 'Marker' && _style.style) {
                                let { image, imageType, imageKey, color } = _style.style;
                                if (image && imageType) {
                                    if (imageType === 'gif') {
                                        entity.setGifImage(image, imageKey);
                                    }
                                }
                                if (color && entity.label && typeof this._options.label === 'boolean') {
                                    entity.label.backgroundColor._value = Color.fromCssColorString(transformRgb(color, 0.6))
                                }
                            }
                        }
                    } else if (this.renderMode === 'point') {
                        setRenderer(entity, {
                            type: 'Marker',
                            style: {
                                type: 'PointIcon',
                                size: 15,
                                ...(isObject(this.markerStyle) ? this.markerStyle : {}),
                            },
                        });
                    }

                    entity.show = show;
                    entity.type = this.modalType || this.key;
                    entity.key = this.key;
                    entity.properties = v.properties || {};

                    this.entities.add(entity);
                }
            });
        }
    }

    // 展示聚合
    showClustering() {
        // 是否聚合
        this.customStyle();

        // 聚合点的判定范围(像素单位)
        this.clustering.pixelRange = this.cluster?.pixelRange || 100;

        // 最小簇大小
        this.clustering.minimumClusterSize = this.cluster?.minimumClusterSize || 2;

        // 默认关闭，延时开启，避免一开始不生效
        this.clustering.enabled = false;
        setTimeout(() => {
            // 解决一开始不聚合的问题
            this.clustering.pixelRange = 0;
            this.clustering.pixelRange = this.cluster?.pixelRange || 100;
            this.clustering.enabled = true;
        }, 50);

        // 高度监听
        this.heightListerHeight();
    }

    heightListerHeight() {
        if (defined(this.removeListenerHeight) && this.removeListenerHeight) {
            this.removeListenerHeight();
            this.removeListenerHeight = undefined;
        }

        const viewer = this.map;
        const _this = this;
        // 监听scene的postRender事件
        this.removeListenerHeight = viewer.scene.postRender.addEventListener(function() {
            // 获取当前的相机高度
            const currentCameraHeight = viewer.camera.positionCartographic.height;

            _this.clustering.enabled = true;
            if (_this.cluster && _this.cluster.height) {
                const { height } = _this.cluster;
                if (currentCameraHeight <= height) {
                    _this.clustering.enabled = false;
                }
            } else if (currentCameraHeight <= (viewer.fitViewHeight || 5000) + 1000) {
                // 当高度小于阈值时禁用聚合
                _this.clustering.enabled = false;
            }
        });
    }

    // 隐藏聚合
    hideClustering() {
        this.clustering.enabled = false;
        if (defined(this.removeListener) && this.removeListener) {
            this.removeListener();
            this.removeListener = undefined;
        }

        if (defined(this.removeListenerHeight) && this.removeListenerHeight) {
            this.removeListenerHeight();
            this.removeListenerHeight = undefined;
        }
    }

    // 自定义样式
    customStyle() {
        let _this = this;
        if (defined(this.removeListener) && this.removeListener) {
            this.removeListener();
            this.removeListener = undefined;
        }

        this.removeListener = this.clustering.clusterEvent.addEventListener(
            (clusteredEntities, cluster) => {
                cluster.label.show = false;
                cluster.billboard.show = true;
                cluster.billboard.id = cluster.label.id;
                cluster.billboard.verticalOrigin = VerticalOrigin.BOTTOM;
                cluster.billboard.heightReference = HeightReference.CLAMP_TO_GROUND;
                cluster.billboard.horizontalOrigin = HorizontalOrigin.CENTER;

                let text = clusteredEntities.length;

                if (_this.cluster) {
                    const { style } = _this.cluster;

                    let image,
                        scale = 1;

                    if (isFunction(style)) {
                        image = style(clusteredEntities);
                    } else if (isObject(style)) {
                        const { icon, iconSrc, type, ...otherStyle } = style;
                        if (iconSrc) {
                            image = customClusterStyle(iconSrc, {
                                type,
                                text,
                                ...otherStyle,
                            });
                        } else {
                            image = getImgUrl(type || 'CommonIcon2', {
                                canvasIcon: icon,
                                color: 'rgb(0,128,255)',
                                hideFooter: true,
                                info: [{ text, fontSize: 24 }],
                                ...otherStyle,
                            });
                        }
                        scale = style?.scale;
                    } else if (typeof style === 'string') {
                        image = getImgUrl('CommonIcon2', {
                            canvasIcon: style,
                            color: 'rgb(0,128,255)',
                            hideFooter: true,
                            info: [{ text, fontSize: 24 }],
                        });
                    }
                    cluster.billboard.image = image;
                    cluster.billboard.scale = scale || 1;
                } else if (isObject(_this.style) || typeof _this.style === 'string') {
                    let icon = _this.style,
                        type = 'CommonIcon2';

                    if (isObject(_this.style) && _this.style.type === 'Marker') {
                        icon = _this.style.style.icon || icon;
                        type = _this.style.style.type || type;
                    }

                    let color = 'rgb(9,220,107)';
                    if (clusteredEntities.length >= 50) {
                        text = '50+';
                        color = 'rgb(220,33,9)';
                    } else if (clusteredEntities.length >= 40) {
                        text = '40+';
                        color = 'rgb(232,119,5)';
                    } else if (clusteredEntities.length >= 30) {
                        text = '30+';
                        color = 'rgb(232,194,4)';
                    } else if (clusteredEntities.length >= 20) {
                        text = '20+';
                        color = 'rgba(20, 232, 4, 1)';
                    } else if (clusteredEntities.length >= 10) {
                        text = '10+';
                        color = 'rgb(0,128,255)';
                    }
                    cluster.billboard.image = getImgUrl(type, {
                        canvasIcon: icon,
                        color,
                        hideFooter: true,
                        info: [{ text, fontSize: 24 }],
                    });
                }
            },
        );
    }

    // 根据条件展示对应的视角
    fitViewByFilter(filter, options = {}) {
        let { features } = this.geoJsonData || {};
        if (features?.length) {
            let geoJsonData = JSON.parse(JSON.stringify(this.geoJsonData));
            if (filter) {
                geoJsonData.features = features.filter(v => filter(v.properties));
            }
            this.map.fitView({
                geoJson: geoJsonData,
                options: { padding: [3000, 3000], ...options },
            });
        }
    }
}

export default FeatureLayer;
